import { HelpPrefs, TourProgress } from './help.models';

export const HELP_STORAGE_PREFIX = 'mmm-iglesia-help:v1';
const TOUR_ID_RE = /^[a-z0-9-]{1,40}$/;
const MAX_STEP = 49;
const MAX_OPENS = 1000;

export function defaultPrefs(): HelpPrefs {
  return { launcherMinimized: false, launcherOpens: 0, tours: {} };
}

export function helpStorageKey(missionId: string, churchId: string, uid: string): string {
  return `${HELP_STORAGE_PREFIX}:${missionId}:${churchId}:${uid}`;
}

function clampInt(value: unknown, min: number, max: number): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : min;
  return Math.min(max, Math.max(min, n));
}

export function sanitizePrefs(raw: unknown): HelpPrefs {
  const prefs = defaultPrefs();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return prefs;
  const obj = raw as Record<string, unknown>;

  prefs.launcherMinimized = obj['launcherMinimized'] === true;
  prefs.launcherOpens = clampInt(obj['launcherOpens'], 0, MAX_OPENS);

  const tours = obj['tours'];
  if (tours && typeof tours === 'object' && !Array.isArray(tours)) {
    for (const [id, value] of Object.entries(tours as Record<string, unknown>)) {
      if (!TOUR_ID_RE.test(id) || !value || typeof value !== 'object') continue;
      const progress = value as Record<string, unknown>;
      const entry: TourProgress = {
        step: clampInt(progress['step'], 0, MAX_STEP),
        done: progress['done'] === true,
      };
      prefs.tours[id] = entry;
    }
  }
  return prefs;
}

/** Lectura defensiva: JSON inválido o storage bloqueado devuelven valores por defecto. */
export function loadPrefs(storage: Storage | null, key: string): HelpPrefs {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return defaultPrefs();
    return sanitizePrefs(JSON.parse(raw));
  } catch {
    return defaultPrefs();
  }
}

/** Escritura defensiva: sin cuota o bloqueado, la ayuda sigue funcionando en memoria. */
export function savePrefs(storage: Storage | null, key: string, prefs: HelpPrefs): boolean {
  try {
    if (!storage) return false;
    storage.setItem(key, JSON.stringify(sanitizePrefs(prefs)));
    return true;
  } catch {
    return false;
  }
}

export function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}
